import React, { useState, useEffect, forwardRef } from 'react';
import { Link } from 'react-router-dom';
import { FaChevronRight, FaChevronDown } from 'react-icons/fa';
import axios from 'axios';

// Sidebar item interface
export interface SidebarItem {
  id: string;
  name: string;
  path?: string;
  icon?: string;
  children?: SidebarItem[];
  parentId?: string;
}

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
  sidebarItems: SidebarItem[];
  googleUser: { name: string; picture: string; email: string } | null;
  onLogout: () => void;
  openLoginModal: () => void;
  headerHeight: number;
}

const SidebarMenuItem: React.FC<{ item: SidebarItem; onClose: () => void; isChild?: boolean }> = ({ item, onClose, isChild = false }) => {
  const [isSubMenuOpen, setIsSubMenuOpen] = useState(false);
  const hasChildren = item.children && item.children.length > 0;

  const handleToggleSubMenu = (e: React.MouseEvent) => {
    if (hasChildren) {
      // Agar parent hai to sirf toggle karo, navigate mat karo
      e.preventDefault(); 
      setIsSubMenuOpen(!isSubMenuOpen);
    } else if (item.path) {
      // Agar child hai to close karo (navigation Link se handle hoga)
      onClose();
    }
  };

  return (
    <li className="list-none">
      <div
        className={`flex items-center justify-between text-gray-700 hover:bg-gray-100 hover:text-green-600 text-md py-2 px-4 cursor-pointer ${
          hasChildren ? 'font-semibold' : ''
        } ${isChild ? 'pl-8' : ''}`}
        onClick={handleToggleSubMenu}
      >
        {/* CHANGE HERE: Agar path hai AUR children NAHI hain, tabhi Link dikhao */}
        {item.path && !hasChildren ? (
          <Link to={item.path} onClick={onClose} className="flex-grow">
            {item.name}
          </Link>
        ) : (
          // Agar children hain, to sirf text dikhao (click pe dropdown khulega div ke onClick se)
          <div className="flex-grow">{item.name}</div>
        )}

        {hasChildren && (
          <FaChevronDown
            className={`text-xs transition-transform ${
              isSubMenuOpen ? 'rotate-180' : 'rotate-0'
            }`}
          />
        )}
      </div>

      {hasChildren && isSubMenuOpen && (
        <ul className="pl-0 mt-0 list-none">
          {item.children?.map(child => (
            <SidebarMenuItem
              key={child.id}
              item={child}
              onClose={onClose}
              isChild={true}
            />
          ))}
        </ul>
      )}
    </li>
  );
};

// ✅ FIXED: always use slug for path
const buildCategoryTree = (categories: any[]): SidebarItem[] => {
  const categoryMap: { [key: string]: SidebarItem } = {};
  const rootCategories: SidebarItem[] = [];

  // Har category ko map me daal do
  categories.forEach(cat => {
    const sidebarItem: SidebarItem = {
      id: cat.id.toString(),
      name: cat.name,
      path: `/category/${cat.id}`,   // ✅ slug ki jagah id use karo
      parentId: cat.parentId ? cat.parentId.toString() : undefined,
      children: [],
    };
    categoryMap[sidebarItem.id] = sidebarItem;
  });

  // Ab parent-child relationship build karo
  categories.forEach(cat => {
    const currentItem = categoryMap[cat.id.toString()];
    if (cat.parentId) {
      const parentItem = categoryMap[cat.parentId.toString()];
      if (parentItem) {
        currentItem.path = `${parentItem.path}/${cat.id}`; // ✅ parent id + child id
        parentItem.children?.push(currentItem);
      }
    } else {
      rootCategories.push(currentItem);
    }
  });

  return rootCategories;
};

export const Sidebar = forwardRef<HTMLDivElement, SidebarProps>(
  ({ isOpen, onClose, sidebarItems: initialSidebarItems, googleUser, onLogout, openLoginModal, headerHeight }, ref) => {
    const [dynamicSidebarItems, setDynamicSidebarItems] = useState<SidebarItem[]>(initialSidebarItems);

    useEffect(() => {
      const fetchCategories = async () => {
        try {
          const response = await axios.get('https://www.ayushvatika.com/api/categories');
          // const response = await axios.get('http://localhost:8484/api/categories');
          console.log("Fetched categories:", response.data); // 🔍 backend se kya aa raha hai
          const categories = response.data;

          const categoryTree = buildCategoryTree(categories);
          console.log("Sidebar items with path:", categoryTree); // 🔍 final path check

          const updatedSidebarItems = initialSidebarItems.map(item => {
            if (item.name === 'Shop By Category') {
              return { ...item, children: categoryTree };
            }
            return item;
          });

          setDynamicSidebarItems(updatedSidebarItems);
        } catch (error) {
          console.error('Failed to fetch categories:', error);
          setDynamicSidebarItems(initialSidebarItems);
        }
      };

      fetchCategories();
    }, [initialSidebarItems]);

    return (
      <>
        {/* Slide-in Sidebar */}
        <div
          ref={ref}
          style={{ top: `${headerHeight}px`, height: `calc(100vh - ${headerHeight}px)` }}
          className={`fixed left-0 w-64 bg-white shadow-lg z-50
            transform transition-transform duration-300 ease-in-out
            ${isOpen ? 'translate-x-0' : '-translate-x-full'}`}
          onMouseLeave={onClose}
        >
          {/* Menu Items */}
          <nav className="p-4 overflow-y-auto h-full pb-20">
            <ul className="list-none p-0 m-0">
              {dynamicSidebarItems.map(item => (
                <SidebarMenuItem key={item.id} item={item} onClose={onClose} />
              ))}
            </ul>

            {/* Account Section */}
            <h3 className="text-lg font-semibold text-gray-700 mt-6 mb-3 border-t pt-4 px-4">Account</h3>
            <ul className="list-none p-0 m-0">
              {googleUser ? (
                <>
                  <li className="mb-2">
                    {/* <Link
                      to="/profile"
                      className="flex items-center justify-between text-gray-800 hover:text-emerald-600 hover:bg-gray-100 text-md py-2 px-4"
                      onClick={onClose}
                    >
                      Profile <FaChevronRight className="text-xs" />
                    </Link> */}
                  </li>
                  <li className="mb-2">
                    <Link
                      to="/settings"
                      className="flex items-center justify-between text-gray-800 hover:text-emerald-600 hover:bg-gray-100 text-md py-2 px-4"
                      onClick={onClose}
                    >
                      Settings <FaChevronRight className="text-xs" />
                    </Link>
                  </li>
                  <li className="mb-2">
                    <button
                      onClick={() => {
                        onLogout();
                        onClose();
                      }}
                      className="w-full text-left flex items-center justify-between text-gray-800 hover:text-emerald-600 hover:bg-gray-100 text-md py-2 px-4"
                    >
                      Log out <FaChevronRight className="text-xs" />
                    </button>
                  </li>
                </>
              ) : (
                <li className="mb-2">
                  <button
                    onClick={() => {
                      openLoginModal();
                      onClose();
                    }}
                    className="w-full text-left flex items-center justify-between text-gray-800 hover:text-emerald-600 hover:bg-gray-100 text-md py-2 px-4"
                  >
                    Login / Register <FaChevronRight className="text-xs" />
                  </button>
                </li>
              )}
            </ul>
          </nav>
        </div>
      </>
    );
  }
);

export default Sidebar;